import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, Tag } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import StickyCTA from "@/components/site/StickyCTA";
import { useReveal } from "@/lib/reveal";
import { ARTICLES_BY_SLUG, ARTICLES } from "@/data/articles";

function renderBlock(block, i) {
  const key = `b-${i}`;
  if (block.type === "h2") {
    return (
      <h2
        key={key}
        className="mt-12 mb-5 font-display text-2xl md:text-3xl text-primary leading-snug"
      >
        {block.content}
      </h2>
    );
  }
  if (block.type === "h3") {
    return (
      <h3
        key={key}
        className="mt-8 mb-3 font-display text-xl md:text-2xl text-primary/95 leading-snug"
      >
        {block.content}
      </h3>
    );
  }
  if (block.type === "p") {
    return (
      <p key={key} className="mt-4 text-base md:text-[17px] text-primary/85 leading-[1.75]">
        {block.content}
      </p>
    );
  }
  if (block.type === "ul") {
    return (
      <ul key={key} className="mt-4 space-y-2.5 pl-1">
        {block.content.map((item, j) => (
          <li
            key={j}
            className="flex items-start gap-3 text-base md:text-[17px] text-primary/85 leading-[1.65]"
          >
            <span
              aria-hidden
              className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--brand-terra))]"
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "cta") {
    return (
      <div key={key} className="mt-12 border border-border bg-[hsl(var(--brand-beige))] p-8 md:p-10 text-center">
        <p className="overline mb-4">Passons à l'action</p>
        <h3 className="font-display text-2xl md:text-3xl text-primary leading-snug max-w-2xl mx-auto">
          {block.content}
        </h3>
        <p className="mt-3 text-sm text-muted-foreground">
          Devis gratuit sous 24h ouvrées · Sans engagement
        </p>
        <div className="mt-6">
          <Link
            to="/#contact"
            data-testid="article-cta"
            className="btn-primary group"
          >
            Demander mon devis
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    );
  }
  return null;
}

export default function ArticlePage() {
  const { slug } = useParams();
  useReveal();
  const article = ARTICLES_BY_SLUG[slug];

  useEffect(() => {
    if (!article) return;
    document.title = article.metaTitle;
    let meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", article.metaDescription);
    window.scrollTo(0, 0);
  }, [article]);

  if (!article) {
    return <Navigate to="/ressources" replace />;
  }

  // Related: 3 other articles (excluding current)
  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <div className="min-h-screen" data-testid={`article-page-${article.slug}`}>
      <Navbar />

      <article className="pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="container-narrow">
          <Link
            to="/ressources"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-[hsl(var(--brand-terra))] transition-colors"
          >
            <ArrowLeft size={14} strokeWidth={1.8} />
            Toutes les ressources
          </Link>

          <header className="mt-8 reveal">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] border border-border rounded-full text-primary/80">
                <Tag size={11} strokeWidth={1.8} />
                {article.category}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock size={13} strokeWidth={1.5} />
                {article.readingTime} de lecture
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-primary leading-[1.1]">
              {article.title}
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              {article.excerpt}
            </p>
          </header>

          <div className="mt-12 reveal">{article.blocks.map(renderBlock)}</div>
        </div>
      </article>

      {/* Related articles */}
      <section className="py-20 bg-[hsl(var(--brand-beige))]">
        <div className="container-wide">
          <div className="flex items-center gap-3 mb-8">
            <span className="rule" />
            <span className="overline">À lire aussi</span>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/ressources/${a.slug}`}
                className="reveal group border border-border bg-white p-7 hover:-translate-y-1 transition-all duration-300"
                data-testid={`related-article-${a.slug}`}
              >
                <span className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand-terra))] font-semibold">
                  {a.category}
                </span>
                <h3 className="mt-4 font-display text-lg text-primary leading-snug group-hover:text-[hsl(var(--brand-terra))] transition-colors">
                  {a.title}
                </h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                  {a.excerpt}
                </p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-primary font-semibold">
                  Lire l'article
                  <ArrowRight size={13} strokeWidth={1.8} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
      <StickyCTA />
    </div>
  );
}
